'use client';

import {
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { JobTemplate, SparkEngine } from '@repo/api-gw-sdk';
import { useEffect, useRef, useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { useDAL } from '@/contexts/useDAL';

interface StartJobFromTemplateDialogProps {
  template: JobTemplate;
  workspaceId: string;
  open: boolean;
  onClose: () => void;
  onJobStarted: () => void;
}

export const StartJobFromTemplateDialog = ({
  template,
  workspaceId,
  open,
  onClose,
  onJobStarted,
}: StartJobFromTemplateDialogProps) => {
  const dal = useDAL();
  const [jobName, setJobName] = useState<string>('');
  const [selectedEngineId, setSelectedEngineId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prevOpenRef = useRef(false);

  // Initialize job name with template name only when dialog transitions from closed to open
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setJobName(template.name);
    }
    prevOpenRef.current = open;
  }, [open, template.name]);

  const { data: sparkEnginesData, isLoading: isLoadingEngines } =
    dal.lakehouse.useListSparkEngines(workspaceId);

  // Filter only running engines
  const runningEngines =
    sparkEnginesData?.sparkEngines?.filter(
      (engine: SparkEngine) => engine.status === 'STARTED'
    ) ?? [];

  const handleSubmit = async () => {
    if (!jobName.trim()) {
      setError('Please enter a job name');
      return;
    }

    if (!selectedEngineId) {
      setError('Please select a Spark engine');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await dal.lakehouse.startSparkJob(workspaceId, {
        sparkEngineId: selectedEngineId,
        name: jobName.trim(),
        sparkSubmitParams: template.sparkSubmitParams,
        ...(template.jobTimeout && { jobTimeout: template.jobTimeout }),
        ...(template.retryPolicy && { retryPolicy: template.retryPolicy }),
      });
      onJobStarted();
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to start job. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setJobName('');
    setSelectedEngineId('');
    setError(null);
    onClose();
  };

  return (
    <MainDialog
      isOpen={open}
      isLoading={isSubmitting}
      header={{
        title: 'Start Job from Template',
        onClose: handleClose,
      }}
      footer={{
        primaryButton: {
          text: isSubmitting ? 'Starting...' : 'Start Job',
          onClick: handleSubmit,
          disabled:
            isSubmitting ||
            isLoadingEngines ||
            runningEngines.length === 0 ||
            !jobName.trim() ||
            !selectedEngineId,
          'data-testid': 'start-job-from-template-btn',
        },
        secondaryButton: {
          text: 'Cancel',
          onClick: handleClose,
          'data-testid': 'cancel-start-job-from-template-btn',
        },
      }}
    >
      <Stack gap='24px' padding='0 40px'>
        <Typography variant='body2' color='text.secondary'>
          Start a new Spark job using the &quot;{template.name}&quot; template.
        </Typography>

        <FormControl fullWidth>
          <FormLabel>Job Name</FormLabel>
          <TextField
            value={jobName}
            onChange={(e) => {
              setJobName(e.target.value);
              setError(null);
            }}
            disabled={isSubmitting}
            size='small'
            placeholder='Enter job name'
            error={!!error && !jobName.trim()}
          />
        </FormControl>

        <FormControl fullWidth error={!!error && !selectedEngineId}>
          <FormLabel>Spark Engine</FormLabel>
          <Select
            value={selectedEngineId}
            onChange={(e) => {
              setSelectedEngineId(e.target.value);
              setError(null);
            }}
            disabled={isLoadingEngines || isSubmitting}
            size='small'
            displayEmpty
            renderValue={(value) => {
              if (!value) {
                return (
                  <Typography color='text.secondary'>
                    Select a Spark engine
                  </Typography>
                );
              }
              const engine = runningEngines.find(
                (e: SparkEngine) => e.id === value
              );
              return engine?.name ?? value;
            }}
          >
            <MenuItem value='' disabled>
              Select a Spark engine
            </MenuItem>
            {isLoadingEngines ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Loading engines...
              </MenuItem>
            ) : runningEngines.length === 0 ? (
              <MenuItem disabled>No started engines available</MenuItem>
            ) : (
              runningEngines.map((engine: SparkEngine) => (
                <MenuItem key={engine.id} value={engine.id}>
                  <Stack>
                    <Typography variant='body2'>{engine.name}</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {engine.engineSize}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))
            )}
          </Select>
          {runningEngines.length === 0 && !isLoadingEngines && (
            <FormHelperText>
              No started Spark engines available. Please start an engine first.
            </FormHelperText>
          )}
        </FormControl>

        {error && (
          <Typography color='error' variant='body2'>
            {error}
          </Typography>
        )}
      </Stack>
    </MainDialog>
  );
};
